import { route, SandboxError, type Route } from '../adapter';
import { matches, newId, now, type SandboxStore } from '../store';
import type { AssistantWrite, BulkAssistantsWrite, EventAssistant, SimilarPerson, StaffContact, StaffContactWrite, Staffing } from '@/lib/staffing';

/** Assistants on the event and the staff directory behind them. */

function digits(value: string | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

function personFor(store: SandboxStore, write: AssistantWrite): EventAssistant['contact'] {
  if (write.staffContactId) {
    const contact = store.staffContacts.find((row) => row._id === write.staffContactId);
    if (!contact) throw new SandboxError(404, 'Staff contact not found.');
    return { _id: contact._id, kind: 'contact', name: contact.name, ...(contact.phone ? { phone: contact.phone } : {}), ...(contact.email ? { email: contact.email } : {}), ...(contact.city ? { city: contact.city } : {}), ...(contact.state ? { state: contact.state } : {}) };
  }
  if (write.userId) {
    const person = store.people.find((row) => row._id === write.userId && row.kind === 'user');
    if (!person) throw new SandboxError(404, 'Person not found.');
    return { _id: person._id, kind: 'user', name: person.name, ...(person.phone ? { phone: person.phone } : {}), ...(person.email ? { email: person.email } : {}) };
  }
  throw new SandboxError(400, 'Pick a person.');
}

function peopleOf(store: SandboxStore): SimilarPerson[] {
  const contacts: SimilarPerson[] = store.staffContacts.map((contact) => ({ _id: contact._id, kind: 'contact', name: contact.name, ...(contact.phone ? { phone: contact.phone } : {}), ...(contact.email ? { email: contact.email } : {}), ...(contact.city ? { city: contact.city } : {}) }));
  const users = store.people.filter((person) => person.kind === 'user');
  return [...contacts, ...users].sort((a, b) => a.name.localeCompare(b.name));
}

export const assistantRoutes: readonly Route[] = [
  route('GET', '/events/:id/assistants', ({ store }) => ({ data: store.assistants })),
  route('POST', '/events/:id/assistants', ({ store, body }) => {
    const write = body as AssistantWrite;
    const assistant: EventAssistant = { ...write, _id: newId(), contact: personFor(store, write), role: write.role ?? 'assistant', status: write.status ?? 'requested', addedBy: 'You' };
    store.assistants = { ...store.assistants, assistants: [...store.assistants.assistants, assistant] };
    return { data: store.assistants };
  }),
  // The sandbox has no categories, so a bulk add applies the role sent.
  route('POST', '/events/:id/assistants/bulk', ({ store, body }) => {
    const write = body as BulkAssistantsWrite;
    const taken = new Set(store.assistants.assistants.filter((row) => row.status !== 'canceled').map((row) => row.userId ?? row.staffContactId));
    const fresh = write.people.filter((person) => !taken.has('userId' in person ? person.userId : person.staffContactId));
    const added = fresh.map((person): EventAssistant => ({ ...person, _id: newId(), contact: personFor(store, person), role: write.role ?? 'assistant', status: write.status ?? 'requested', ...(write.hiredBy ? { hiredBy: write.hiredBy } : {}), addedBy: 'You' }));
    store.assistants = { ...store.assistants, assistants: [...store.assistants.assistants, ...added] };
    return { data: { ...store.assistants, added: added.length, skipped: write.people.length - added.length } };
  }),
  route('PATCH', '/events/:id/assistants/:assistantId', ({ store, params, body }) => {
    const patch = body as Partial<AssistantWrite>;
    store.assistants = { ...store.assistants, assistants: store.assistants.assistants.map((row) => (row._id === params['assistantId'] ? { ...row, ...patch, ...(patch.staffContactId || patch.userId ? { contact: personFor(store, { ...row, ...patch }) } : {}) } : row)) };
    return { data: store.assistants };
  }),
  route('DELETE', '/events/:id/assistants/:assistantId', ({ store, params }) => {
    store.assistants = { ...store.assistants, assistants: store.assistants.assistants.filter((row) => row._id !== params['assistantId']) };
    return { data: store.assistants };
  }),
  route('PUT', '/events/:id/staffing', ({ store, body }) => {
    store.assistants = { ...store.assistants, staffing: body as Staffing };
    return { data: store.assistants.staffing };
  }),
  route('GET', '/staff-contacts', ({ store, query }) => {
    if (query.get('people') === '1') return { data: peopleOf(store) };
    const similar = query.get('similar');
    if (similar !== null) {
      const phone = digits(query.get('phone') ?? undefined);
      const email = (query.get('email') ?? '').toLowerCase();
      const words = similar.toLowerCase().split(/\s+/).filter((word) => word.length > 1);
      const hits = peopleOf(store).filter((person) => (phone && digits(person.phone) === phone) || (email && person.email?.toLowerCase() === email) || (words.length > 0 && words.every((word) => person.name.toLowerCase().includes(word))) || (words.length > 1 && words.some((word) => word.length > 3 && person.name.toLowerCase().includes(word))));
      return { data: hits.slice(0, 6) };
    }
    return { data: store.staffContacts.filter((contact) => matches(query.get('search'), contact.name, contact.city, contact.skills.join(' '), contact.email)) };
  }),
  route('GET', '/staff-contacts/:id', ({ store, params }) => {
    const contact = store.staffContacts.find((row) => row._id === params['id']);
    if (!contact) throw new SandboxError(404, 'Staff contact not found.');
    return { data: contact };
  }),
  route('GET', '/staff-contacts/:id/events', ({ store, params }) => ({
    data: store.assistants.assistants
      .filter((row) => row.staffContactId === params['id'])
      .map((row) => ({ _id: store.eventId, eventDate: store.event.eventDate, programName: store.event.programName, accountName: store.event.account?.name, city: store.event.startVenue?.city, state: store.event.startVenue?.state, role: row.role, status: row.status })),
  })),
  route('POST', '/staff-contacts', ({ store, body }) => {
    const write = body as StaffContactWrite;
    const contact: StaffContact = { skills: [], isActive: true, source: 'facilitator', ...write, _id: newId(), updatedAt: now() };
    store.staffContacts = [...store.staffContacts, contact];
    return { data: contact };
  }),
  route('PATCH', '/staff-contacts/:id', ({ store, params, body }) => {
    const index = store.staffContacts.findIndex((row) => row._id === params['id']);
    const existing = store.staffContacts[index];
    if (!existing) throw new SandboxError(404, 'Staff contact not found.');
    const updated: StaffContact = { ...existing, ...(body as Partial<StaffContactWrite>), updatedAt: now() };
    store.staffContacts[index] = updated;
    return { data: updated };
  }),
  route('DELETE', '/staff-contacts/:id', ({ store, params }) => {
    store.staffContacts = store.staffContacts.filter((row) => row._id !== params['id']);
    return { data: null };
  }),
];
